import { useEffect, useState, useCallback, useRef } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { logger } from "@/lib/logger";

const DEFAULT_VALUE = true;
const PREF_PATH = "preferences.hideDeactivatedUsers";

// In-process cache so multiple hook instances stay in sync within one tab
// without each one re-reading Firestore.
let memoryValue: boolean | null = null;
const subscribers = new Set<(v: boolean) => void>();
const broadcast = (v: boolean) => {
  memoryValue = v;
  subscribers.forEach(cb => cb(v));
};

/**
 * Persists the admin's "hide deactivated users in dropdowns" preference under
 * users/{uid}.preferences.hideDeactivatedUsers in Firestore.
 *
 * - Reads once per session (cached in module memory).
 * - Writes optimistically: UI updates instantly, Firestore is updated in background.
 * - Returns the default (true) until the first read completes; this matches prior
 *   behavior so dropdowns don't briefly show deactivated users.
 */
export function useHideDeactivatedUsers(): [boolean, (value: boolean) => void] {
  const { currentUser } = useAuth();
  const [value, setLocal] = useState<boolean>(memoryValue ?? DEFAULT_VALUE);
  const loadedFor = useRef<string | null>(null);

  // Subscribe to in-process changes from sibling hook instances
  useEffect(() => {
    const cb = (v: boolean) => setLocal(v);
    subscribers.add(cb);
    return () => { subscribers.delete(cb); };
  }, []);

  // Load from Firestore once per user
  useEffect(() => {
    const uid = currentUser?.id;
    if (!uid) return;
    if (loadedFor.current === uid) return;
    loadedFor.current = uid;

    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "users", uid));
        if (cancelled) return;
        const data = snap.data() as { preferences?: { hideDeactivatedUsers?: boolean } } | undefined;
        const stored = data?.preferences?.hideDeactivatedUsers;
        const next = typeof stored === "boolean" ? stored : DEFAULT_VALUE;
        broadcast(next);
      } catch (err) {
        logger.error("Failed to load hideDeactivatedUsers preference:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [currentUser?.id]);

  const update = useCallback((next: boolean) => {
    // Optimistic UI update
    broadcast(next);
    const uid = currentUser?.id;
    if (!uid) return;
    updateDoc(doc(db, "users", uid), { [PREF_PATH]: next }).catch(err => {
      logger.error("Failed to persist hideDeactivatedUsers preference:", err);
    });
  }, [currentUser?.id]);

  return [value, update];
}
